import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveCurrentPathForLoginRedirect } from '../services/loginRedirect/loginRedirect';
import fetchFromApiServer from '../services/api';

/**
 * Custom hook to handle session activity tracking and automatic logout
 * @param {Object} options - Configuration options
 * @param {number} options.timeoutMinutes - Session timeout in minutes (default: 10)
 * @param {string} options.logoutEndpoint - API endpoint for logout (default: 'users/logout')
 * @param {string} options.loginPath - Path to redirect to on logout (default: '/login')
 * @param {boolean} options.enabled - Whether session tracking is enabled (default: true)
 */
const useSessionActivity = ({
  timeoutMinutes = 60,
  logoutEndpoint = 'users/logout',
  loginPath = '/login',
  enabled = true,
} = {}) => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isLoggingOutRef = useRef(false);

  // Handle logout process
  const handleLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;

    isLoggingOutRef.current = true;

    try {
      // Clear the timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Save current path for login redirect before logout
      saveCurrentPathForLoginRedirect(true);

      await fetchFromApiServer('POST', logoutEndpoint, {});

      // Redirect to login page
      navigate(loginPath, { replace: true });
    } catch (error) {
      // Even if logout API fails, redirect to login
      navigate(loginPath, { replace: true });
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [logoutEndpoint, loginPath, navigate]);

  // Reset the timeout when user is active
  const resetTimeout = useCallback(() => {
    if (!enabled || isLoggingOutRef.current) return;

    lastActivityRef.current = Date.now();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(
      () => {
        handleLogout();
      },
      timeoutMinutes * 60 * 1000
    );
  }, [timeoutMinutes, enabled, handleLogout]);

  // Manual logout function
  const manualLogout = useCallback(() => {
    handleLogout();
  }, [handleLogout]);

  // Set up event listeners for user activity
  useEffect(() => {
    if (!enabled) return;

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      resetTimeout();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initial timeout setup
    resetTimeout();

    // Cleanup function
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, resetTimeout]);

  return {
    manualLogout,
    resetTimeout,
  };
};

export default useSessionActivity;
